import { Stack, Typography } from '@mui/material';
import { GridColDef, GridRow } from '@mui/x-data-grid-pro';

import AppLink from '@/components/AppLink';
import DataGrid from '@/components/DataGrid';
import { BasetenPageEnum } from '@/routes';
import Actions from '@/sections/Training/Actions';
import { getTrainingJobElapsedTime, mapTrainingJobStatusToChip } from '@/sections/Training/utils';
import VersionId from '@/sections/Workspace/components/VersionId';
import moment from 'moment';

import { dataGridProps, dataGridStyles } from '../../styled';
import { InstanceTypeName } from '../InstanceTypeName';
import { WorkspaceTrainingJobFragment } from '../__generated__/fragments.generated';
import { MAX_ITEMS_TO_SHOW } from '../constants';
import { ListProps } from '../types';
import { getInstanceTypeName } from '../utils';

const ROW_HEIGHT = 67;

function Table({ trainingJobs, height, minimal }: ListProps) {
  const columns: GridColDef<WorkspaceTrainingJobFragment>[] = [
    {
      field: 'name',
      headerName: 'Name',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: ({ row }) => (
        <Stack width="100%" gap={1} direction="row" alignItems="center">
          <Stack gap={0.5} justifyContent="center">
            <Stack direction="row" gap={1}>
              <Typography noWrap variant="h5">
                {row.name}
              </Typography>
              <VersionId id={row.id} containerProps={{ variant: 'h5' }} />
            </Stack>
            <Typography color="text.subtle" variant="body2">
              {row.project.name} • Started {moment(row.created).format('M/D [at] h:mm:ss A')}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }) => mapTrainingJobStatusToChip(row.status.value),
    },
    {
      field: 'instanceType',
      headerName: 'Instance type',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }: { row: WorkspaceTrainingJobFragment }) => (
        <InstanceTypeName>{getInstanceTypeName(row.instanceType)}</InstanceTypeName>
      ),
    },
    {
      field: 'elapsedTime',
      headerName: 'Elapsed time',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }: { row: WorkspaceTrainingJobFragment }) =>
        getTrainingJobElapsedTime(row),
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => <Actions trainingJob={row} variant="minimal" />,
    },
  ];

  return (
    <Stack data-cy="training-table-content" height={minimal ? 'auto' : height}>
      <DataGrid<WorkspaceTrainingJobFragment>
        rows={minimal ? trainingJobs.slice(0, MAX_ITEMS_TO_SHOW) : trainingJobs}
        columns={columns}
        rowHeight={ROW_HEIGHT}
        getRowId={(row) => row.id}
        sx={dataGridStyles}
        // Row virtualization does not work with the autoHeight prop enabled.
        // https://mui.com/x/react-data-grid/virtualization/#row-virtualization
        disableAutoHeight={!minimal}
        {...dataGridProps}
        slots={{
          row: (props) => (
            <AppLink
              page={BasetenPageEnum.TrainingJob}
              params={{ trainingJobId: props.row.id }}
              key={props.row}
              underline="none"
              sx={{ color: 'inherit', '&:hover': { textDecoration: 'none' } }}
            >
              <GridRow {...props} />
            </AppLink>
          ),
        }}
      />
    </Stack>
  );
}

export default Table;
